/**
 * Markdown-wrap helpers for the composer toolbar + keyboard shortcuts.
 *
 * Each helper takes the current textarea value + selection range, returns
 * the new value + new selection range. The caller is responsible for
 * applying setSelectionRange after the textarea re-renders.
 *
 * Wrap rules (Discord-style markdown subset):
 *   bold        — **text**
 *   italic      — *text*
 *   underline   — __text__
 *   strike      — ~~text~~
 *   code        — `text`     (inline; no triple-backtick block here —
 *                              the user types ``` explicitly when they
 *                              want a code block)
 *   spoiler     — ||text||
 *   quote       — line-prefix "> "
 *   link        — [label](url)
 *
 * If there's no selection, we insert the markers and place the caret in
 * the middle so the user can type immediately ("**|**" / "`|`" etc).
 * For an already-wrapped selection (perfect match), we unwrap — that
 * way the button toggles like Word's bold.
 */
import i18n from '@/i18n';

export interface WrapResult {
  value: string;
  /** Selection start to apply after the textarea updates. */
  selStart: number;
  /** Selection end. */
  selEnd: number;
}

function wrapInline(
  source: string,
  selStart: number,
  selEnd: number,
  marker: string,
): WrapResult {
  return wrapInlineAsym(source, selStart, selEnd, marker, marker);
}

function wrapInlineAsym(
  source: string,
  selStart: number,
  selEnd: number,
  open: string,
  close: string,
): WrapResult {
  const selected = source.slice(selStart, selEnd);
  // Toggle: if the selection (or the slice that the selection MAYBE
  // already lives inside) is already wrapped with these markers, unwrap.
  const beforeContext = source.slice(
    Math.max(0, selStart - open.length),
    selStart,
  );
  const afterContext = source.slice(selEnd, selEnd + close.length);
  if (beforeContext === open && afterContext === close) {
    const newValue =
      source.slice(0, selStart - open.length) +
      selected +
      source.slice(selEnd + close.length);
    return {
      value: newValue,
      selStart: selStart - open.length,
      selEnd: selEnd - open.length,
    };
  }
  if (
    selected.length >= open.length + close.length &&
    selected.startsWith(open) &&
    selected.endsWith(close)
  ) {
    const inner = selected.slice(open.length, selected.length - close.length);
    const newValue = source.slice(0, selStart) + inner + source.slice(selEnd);
    return {
      value: newValue,
      selStart,
      selEnd: selStart + inner.length,
    };
  }
  // Plain wrap. Empty selection → caret lands between markers.
  const wrapped = open + selected + close;
  const newValue =
    source.slice(0, selStart) + wrapped + source.slice(selEnd);
  if (selected.length === 0) {
    return {
      value: newValue,
      selStart: selStart + open.length,
      selEnd: selStart + open.length,
    };
  }
  return {
    value: newValue,
    selStart: selStart + open.length,
    selEnd: selStart + open.length + selected.length,
  };
}

export const wrapBold = (s: string, a: number, b: number): WrapResult =>
  wrapInline(s, a, b, '**');
export const wrapItalic = (s: string, a: number, b: number): WrapResult =>
  wrapInline(s, a, b, '*');
export const wrapUnderline = (s: string, a: number, b: number): WrapResult =>
  wrapInline(s, a, b, '__');
export const wrapStrike = (s: string, a: number, b: number): WrapResult =>
  wrapInline(s, a, b, '~~');
export const wrapCode = (s: string, a: number, b: number): WrapResult =>
  wrapInline(s, a, b, '`');
export const wrapSpoiler = (s: string, a: number, b: number): WrapResult =>
  wrapInline(s, a, b, '||');

/**
 * Quote — line-mode. Prefixes every line that intersects the selection
 * with "> ". If every line already starts with "> ", strips it (toggle).
 * Empty selection: just prefixes the current line.
 */
export function wrapQuote(
  source: string,
  selStart: number,
  selEnd: number,
): WrapResult {
  // Expand the selection to whole lines so we operate on a clean block.
  const blockStart = source.lastIndexOf('\n', selStart - 1) + 1;
  let blockEnd = source.indexOf('\n', selEnd);
  if (blockEnd === -1) blockEnd = source.length;
  const block = source.slice(blockStart, blockEnd);
  const lines = block.split('\n');
  const allQuoted = lines.every((line) => /^>\s?/.test(line));
  const next = allQuoted
    ? lines.map((line) => line.replace(/^>\s?/, '')).join('\n')
    : lines.map((line) => '> ' + line).join('\n');
  const value = source.slice(0, blockStart) + next + source.slice(blockEnd);
  const delta = next.length - block.length;
  return {
    value,
    selStart: blockStart,
    selEnd: blockEnd + delta,
  };
}

/**
 * Link — wraps the selection as a markdown link with placeholder URL.
 * If the selection is itself a URL, swap to a `[label](url)` with the URL
 * as both label and target (caret on label so the user can rename).
 */
export function wrapLink(
  source: string,
  selStart: number,
  selEnd: number,
): WrapResult {
  const selected = source.slice(selStart, selEnd);
  const isUrl = /^https?:\/\/\S+$/.test(selected);
  const label = selected.length === 0 ? i18n.t('lib2.format.linkText') : selected;
  const url = isUrl ? selected : 'https://';
  const inserted = `[${label}](${url})`;
  const value = source.slice(0, selStart) + inserted + source.slice(selEnd);
  // Caret on the URL placeholder so the user can paste over it (or on
  // the label if the selection was an URL — the URL is the right thing).
  const cursorStart = isUrl ? selStart + 1 : selStart + 1 + label.length + 2;
  const cursorEnd = isUrl
    ? selStart + 1 + label.length
    : selStart + inserted.length - 1;
  return { value, selStart: cursorStart, selEnd: cursorEnd };
}
